import { forwardRef, useId, useRef, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import useActiveOption from '~/hooks/useActiveOption'
import useIsFocusWithin from '~/hooks/useIsFocusWithin/useIsFocusWithin'
import useIsOpen from '~/hooks/useIsOpen'

import Icon from '../../Icon'
import SelectionMenu from '../../Selections/SelectionMenu'
import { SelectionMenuOption } from '../../Selections/SelectionMenu/type'

import Input from '../Input'

import useFilteredOptions from './useFilteredOptions'

import {
  ArrowButton,
  AutocompleteMultiselectionContainer,
  IconButton,
  SpinningIcon
} from './styles'

import { AutocompleteMultiselectionProps } from './type'

export default forwardRef<HTMLInputElement, AutocompleteMultiselectionProps>(
  function AutocompleteMultiselection(
    {
      heightClass = 'regular',
      selectedOptions = [],
      value,
      disabled,
      informational,
      options,
      statusClass,
      onChange,
      onSelect,
      ...props
    },
    ref
  ) {
    const selectionMenuId = useId()

    const { isOpen, open, close } = useIsOpen(false)

    const toggleList = () => (isOpen ? close() : open())

    const defaultRef = useRef<HTMLInputElement>(null)

    const inputRef = ref || defaultRef

    const focusInput = () => {
      if ('current' in inputRef) {
        inputRef.current?.focus()
      }
    }

    const clearValue = () => {
      onChange?.('')

      if ('current' in inputRef) {
        inputRef.current?.focus()
      }
    }

    const { t } = useTranslation()

    const rightAreaElement = () =>
      !informational && (
        <>
          {value && statusClass !== 'processing' && !disabled && isOpen && (
            <IconButton onClick={clearValue} title={t('multiselection.clear')}>
              <Icon iconName='Rubber' icSize='small' />
            </IconButton>
          )}

          {statusClass === 'processing' && <SpinningIcon iconName='Spinner' />}

          <ArrowButton
            isOpen={isOpen}
            disabled={disabled}
            tabIndex={-1}
            onClick={toggleList}
            title={
              isOpen
                ? t('multiselection.hide-options')
                : t('multiselection.show-options')
            }
          >
            <Icon
              iconName={
                disabled ||
                (selectedOptions.length === 0 && options.length === 0)
                  ? 'ArrowDownDoubleCircleDashed'
                  : 'ArrowDownDoubleCircle'
              }
              preserveFocusOnParent
            />
          </ArrowButton>
        </>
      )

    const [collapseSelected, setCollapseSelected] = useState(false)

    const filteredOptions = useFilteredOptions({
      options,
      selectedOptions,
      value,
      collapseSelected
    })

    const {
      activeOptionIndex,
      activeOptionId,
      setNextActiveOption,
      setPreviousActiveOption,
      clearActiveOption
    } = useActiveOption({
      value,
      isOpen,
      options: filteredOptions,
      collapseSelected
    })

    const handleSelect = (selected: SelectionMenuOption) => {
      focusInput()

      if (selected.collapsable) {
        setCollapseSelected(
          (previousCollapseSelected) => !previousCollapseSelected
        )

        return
      }

      onSelect?.(selected)
    }

    const onKeyDown: React.KeyboardEventHandler = (event) => {
      open()

      const selectActiveOption = () =>
        filteredOptions[activeOptionIndex] &&
        handleSelect(filteredOptions[activeOptionIndex])

      const actionsByKey = {
        Enter: selectActiveOption,
        ArrowDown: setNextActiveOption,
        ArrowUp: setPreviousActiveOption
      }

      if (event.key in actionsByKey) {
        event.preventDefault()

        actionsByKey[event.key as keyof typeof actionsByKey]()
      }
    }

    const { isFocusWithin, containerProps } = useIsFocusWithin()

    if (isOpen && !isFocusWithin) {
      close()
    }

    const displayValue = (() => {
      if (informational) {
        return selectedOptions.map((option) => option.value).join(' | ')
      }

      if (isFocusWithin || selectedOptions.length === 0) {
        return value
      }

      return selectedOptions.length === 1
        ? selectedOptions[0]?.label || selectedOptions[0]?.value || ''
        : `${selectedOptions.length} ${t('multiselection.selections')}`
    })()

    return (
      <AutocompleteMultiselectionContainer {...containerProps}>
        <Input
          {...props}
          onChange={onChange}
          statusClass={statusClass}
          rightAreaElement={rightAreaElement}
          onKeyDown={onKeyDown}
          disabled={disabled}
          informational={informational}
          heightClass={heightClass}
          value={displayValue}
          role='combobox'
          aria-activedescendant={activeOptionId}
          aria-haspopup='listbox'
          aria-controls={selectionMenuId}
          aria-expanded={isOpen}
          aria-busy={statusClass === 'processing'}
          onFocus={open}
          ref={inputRef}
          autoComplete='off'
        />

        {isOpen && !disabled && !informational && (
          <SelectionMenu
            id={selectionMenuId}
            options={filteredOptions}
            onSelect={handleSelect}
            selectedOptions={selectedOptions}
            activeOptionId={activeOptionId}
            statusClass={statusClass}
            onMouseMove={clearActiveOption}
          />
        )}
      </AutocompleteMultiselectionContainer>
    )
  }
)
